import React, { useContext, useEffect, useState } from "react";
import UserContext from "../../context/UserContext";
import { useCompanies } from "../../context/CompanyContext"; // <-- Added Company Context
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { getAllBasesInContainersWithLogoutDateByCompanyId } from "../../api/baseApi";
import {
  getAllDevicesByCompanyId,
  getAllDevicesWithNoContainerByCompanyId,
} from "../../api/deviceApi";
import {
  getAllContainersBySiteName,
  getAllSitesByCompanyId,
  getAllContainersByCompanyId,
} from "../../api/locationApi";
/* import ShowCountOfDevices from "../Shared/ShowCountOfDevices/ShowCountOfDevices"; */
import ConnectDeviceToContainerButton from "../Shared/ConnectDeviceToContainerButton/ConnectDeviceToContainerButton";
import { FormSelect, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import {
  EditOutlined,
  DeleteOutlined,
} from "../../helpers/icons/antDesignIcons";
import "./DevicesPage.scss";
import useTranslation from "../../hooks/useTranslation";
import TooltipButton from "../Shared/TooltipButton/TooltipButton";
import PageHeader from "../Shared/PageHeader/PageHeader";
import USER_ROLES from "../../constants/userRoles"; // <-- Added User Roles Constant

const getStatusForBase = (logoutdate) => {
  const currentTime = moment();
  const logoutTime = moment.utc(logoutdate).local(); // Convert UTC to local time
  const timeDifference = currentTime.diff(logoutTime, "minutes"); // difference in minutes

  if (timeDifference > 20) {
    return {
      color: "red",
      text: "Offline",
      logoutTime: logoutTime.format("YYYY-MM-DD HH:mm:ss"),
    };
  } else {
    return {
      color: "green",
      text: "Online",
      logoutTime: logoutTime.format("YYYY-MM-DD HH:mm:ss"),
    };
  }
};

const DevicesPage = () => {
  const { user } = useContext(UserContext);
  const { companies } = useCompanies(); // <-- Get companies list
  const navigate = useNavigate();
  const { t } = useTranslation("devicesPage");

  // DYNAMIC COMPANY ID STATE (Defaults to user's company)
  const [activeCompanyId, setActiveCompanyId] = useState(user.company_id);

  // DEVICES STATES
  const [basesDataForStatus, setBasesDataForStatus] = useState([]);
  const [devices, setDevices] = useState([]);
  const [originalDevices, setOriginalDevices] = useState([]); // Original list of devices
  const [devicesForConnectDevice, setDevicesForConnectDevice] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [devicesWithoutContainer, setDevicesWithoutContainer] = useState([]);
  
  const devicesTableHeaders = {
    probeid: t("probeId"),
    containername: t("container"),
    sitename: t("site"),
    probe_type: t("deviceType"),
    /* status: "Státusz", */
    /* sens_number: "Szenzorok száma", */
    edit: t("edit"),
    delete: t("removeFromContainer"),
  };

  // SITES AND CONTAINERS STATES
  const [sites, setSites] = useState([]);
  const [sitesForConnectDevice, setSitesForConnectDevice] = useState([]);
  const [containers, setContainers] = useState([]);
  const [originalContainers, setOriginalContainers] = useState([]); // Original list of containers
  const [containersForConnectDevice, setContainersForConnectDevice] = useState([]);

  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [filteredContainers, setFilteredContainers] = useState([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);

  // Re-fetch all data when activeCompanyId changes
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch Devices
        const devicesData = await getAllDevicesByCompanyId(activeCompanyId);
        setDevices(devicesData);
        setOriginalDevices(devicesData);
        setDevicesForConnectDevice(devicesData);

        // Fetch Base Status
        const basesData = await getAllBasesInContainersWithLogoutDateByCompanyId(activeCompanyId);
        setBasesDataForStatus(basesData);

        // Fetch Devices without container
        const devicesNoContainer = await getAllDevicesWithNoContainerByCompanyId(activeCompanyId);
        setDevicesWithoutContainer(devicesNoContainer);

        // Fetch Sites and Containers
        const sitesData = await getAllSitesByCompanyId(activeCompanyId);
        setSites(sitesData.data);
        setSitesForConnectDevice(sitesData.data);
        
        const containersData = await getAllContainersByCompanyId(activeCompanyId);
        setContainers(containersData.data);
        setOriginalContainers(containersData.data);
        setContainersForConnectDevice(containersData.data);
        setFilteredContainers(containersData.data);

        // Reset local filters on company change
        setSelectedSite("all");
        setSelectedContainer("all");

      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    fetchAllData();
  }, [activeCompanyId]);

  useEffect(() => {
    if (selectedSite === null || selectedSite === "all") {
      setFilteredDevices(devices);
    } else {
      setFilteredDevices(
        devices.filter((device) => device.sitename === selectedSite)
      );
    }
  }, [selectedSite, devices]);

  useEffect(() => {
    if (selectedContainer === null || selectedContainer === "all") {
      setFilteredDevices(devices);
    } else {
      setFilteredDevices(
        devices.filter(
          (device) =>
            device.containerid &&
            device.containerid.toString() === selectedContainer
        )
      );
    }
  }, [selectedContainer, devices]);

  const handleSiteChange = async (e) => {
    const siteName = e.target.value;
    setSelectedSite(siteName);

    if (siteName === "all") {
      setDevices(originalDevices);
      setFilteredContainers(originalContainers);
      setSelectedContainer("all");
      return;
    }

    const site = sites.find((site) => site.sitename === siteName);
    if (!site) {
      console.error("Error: Site not found");
      return;
    }

    try {
      const containersData = await getAllContainersBySiteName(siteName);
      if (Array.isArray(containersData)) {
        setContainers(containersData);
        setFilteredContainers(containersData);
      } else {
        setContainers([]);
        setFilteredContainers([]);
      }

      const devicesData = await getAllDevicesByCompanyId(activeCompanyId);
      if (siteName === "all") {
        setFilteredDevices(devicesData);
      } else {
        setDevices(
          devicesData.filter((device) => device.sitename === siteName)
        );
      }
    } catch (error) {
      console.error("Error fetching containers: ", error);
    }
  };

  const handleContainerChange = async (e) => {
    const containerName = e.target.value;
    setSelectedContainer(containerName);

    try {
      const devicesData = await getAllDevicesByCompanyId(activeCompanyId);

      if (!Array.isArray(devicesData)) {
        console.error("Error: devicesData is not an array");
        return;
      }

      if (containerName === "all") {
        setDevices(
          devicesData.filter((device) => device.sitename === selectedSite)
        );
      } else {
        setDevices(
          devicesData.filter((device) => device.containername === containerName)
        );
      }
    } catch (error) {
      console.error("Error fetching containers: ", error);
    }
  };

  const handleMoreInfo = (device) => {
    navigate(`/devices/${device.probeid}`);
  };

  return (
    <div className="container devices-page__container navbar-margin">
      <PageHeader
        title={t("devices_page_title")}
        helperTitle={t("devices_page_title")}
        helperBody={t("devices_page_helper")}
      />
      <div className="devices-page__content">
        <div className="devices-page__header">
          <div className="devices-page__header__filter__wrapper">
            
            {/* COMPANY SELECTOR FOR SUPER ADMINS */}
            {user.roles === USER_ROLES.SUPER_ADMIN && (
              <div className="devices-page__header__filter">
                <span className="devices-page__header__filter__label">
                  {t("company") || "Company"}:
                </span>
                <FormSelect
                  className="devices-page__header__filter__select"
                  value={activeCompanyId}
                  onChange={(e) => setActiveCompanyId(e.target.value)}
                >
                  <option value="" disabled>{t("company") || "Company"}</option>
                  {companies.map((company) => (
                    <option key={company.companyid} value={company.companyid}>
                      {company.companyname}
                    </option>
                  ))}
                </FormSelect>
              </div>
            )}

            <div className="devices-page__header__filter">
              <span className="devices-page__header__filter__label">
                {t("sites")}:
              </span>
              <FormSelect
                id="devices-devicestype"
                className="devices-page__header__filter__select"
                value={selectedSite || "all"}
                onChange={handleSiteChange}
              >
                <option value="all">{t("all")}</option>
                {sites.map((site) => (
                  <option key={site._id} value={site.sitename}>
                    {site.sitename}
                  </option>
                ))}
              </FormSelect>
            </div>
            <div className="devices-page__header__filter">
              <span className="devices-page__header__filter__label">
                {t("containers")}:
              </span>
              <FormSelect
                id="devices-page__filter__location"
                className="devices-page__header__filter__select"
                value={selectedContainer || "all"}
                onChange={handleContainerChange}
              >
                <option value="all">{t("all")}</option>
                {filteredContainers.map((container) => (
                  <option
                    key={container.containername}
                    value={container.containerid}
                  >
                    {container.containername}
                  </option>
                ))}
              </FormSelect>
            </div>
          </div>
          
          {/* RESTRICTED CONNECT BUTTON */}
          {(user.roles === USER_ROLES.SUPER_ADMIN || user.roles === USER_ROLES.COMPANY_ADMIN) && (
            <ConnectDeviceToContainerButton
              deviceList={devicesWithoutContainer}
              containerList={containersForConnectDevice}
              siteList={sitesForConnectDevice}
            />
          )}

        </div>
        {/* <ShowCountOfDevices companyId={activeCompanyId} /> */}
        <div className="table__wrapper">
          <Table
            headers={devicesTableHeaders}
            data={filteredDevices}
            className="devices-page__table"
          >
            <thead className="sticky-table-header">
              <tr className="dpt-header-tr">
                {Object.keys(devicesTableHeaders).map((key) => (
                  <th key={key} id={`dpt-${key}-header`}>
                    {devicesTableHeaders[key]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* If there are no devices in the container, display a message */}
              {devices.length === 0 ? (
                <tr>
                  <td
                    colSpan={Object.keys(devicesTableHeaders).length}
                    className="no-data-row"
                  >
                    {t("noData")}
                  </td>
                </tr>
              ) : (
                devices.map((device, index) => (
                  <tr key={index} className="dpt-row">
                    {Object.keys(devicesTableHeaders).map((key) => {
                      const isProbeIdNull =
                        key === "probeid" && device[key] == null;
                      const status = getStatusForBase(device.logoutdate);

                      return (
                        <td
                          key={key}
                          id={`dpt-${key}-data`}
                          className={`table-data devices-page__table-data ${
                            key === "probeid" && !isProbeIdNull
                              ? "table-link-button"
                              : ""
                          }`}
                          style={{
                            cursor:
                              key === "probeid" && isProbeIdNull
                                ? "default"
                                : "pointer",
                          }}
                          onClick={
                            key === "probeid" && !isProbeIdNull
                              ? () => handleMoreInfo(device)
                              : null
                          }
                        >
                          {key === "edit" ? (
                            <TooltipButton
                              variant="contained"
                              tooltipKey="tooltipFeatureNotAvailable"
                              className="devices-page-list__edit-button"
                            >
                              <EditOutlined />
                            </TooltipButton>
                          ) : key === "delete" ? (
                            <TooltipButton
                              variant="contained"
                              tooltipKey="tooltipFeatureNotAvailable"
                              className="devices-page-list__delete-button"
                            >
                              <DeleteOutlined />
                            </TooltipButton>
                          ) : key === "probe_type" ? (
                            typeof device[key] === "string" ? (
                              device[key].trimEnd()
                            ) : (
                              device[key]
                            )
                          ) : key === "probeid" &&
                            device[key] == null &&
                            device["baseid"] ? (
                            device["containername"] ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`status-tooltip-${index}`}>
                                    <span style={{ color: status.color }}>
                                      ●
                                    </span>{" "}
                                    {status.text}
                                    <br />
                                    {t("last_logout_date")}: {status.logoutTime}
                                  </Tooltip>
                                }
                              >
                                <span>{parseInt(device["baseid"], 10)}</span>
                              </OverlayTrigger>
                            ) : (
                              <span>{parseInt(device["baseid"], 10)}</span>
                            )
                          ) : (
                            device[key] || "-"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default DevicesPage;